import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import * as numeral from 'numeral';
import * as _ from 'underscore';

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS, PATHS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { DailyStats, DailyStatsComponent } from '../daily-stats/daily-stats.component';

@Component({
	selector: 'app-daily-stats-v1',
	styleUrls: ['./daily-stats-v1.component.css'],
	templateUrl: './daily-stats-v1.component.html'
})
export class DailyStatsV1Component extends DailyStatsComponent implements OnInit, WidgetCore, OnDestroy {
	public reloadSubscription;

	@ViewChild('chart', { static: true }) chartElement: ElementRef;

	constructor(
		protected analyticsService: AnalyticsService,
		protected analyticsDataModel: AnalyticsDataModel,
		protected colorUtilityService: ColorUtilityService
	) {
		super(analyticsService, analyticsDataModel, colorUtilityService);
	}

	ngOnInit(): void {
		this.colorsconfig = JSON.parse(decodeURI(this.colorsconfig));
		this.requestData();
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	processColors(): void {
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.statsColors = this.colors.split(',');
	}

	mapResponseDataToObj(responseData: any): void {
		for (const responseObj of responseData) {
			const dailyStatsObj: DailyStats = {} as DailyStats;
			dailyStatsObj.date = moment(responseObj.date).format('MM/DD/YYYY');
			dailyStatsObj.totalImpressions =
				responseObj.total_impressions != null ? parseInt(responseObj.total_impressions, 10) : 0;
			dailyStatsObj.ctr = responseObj.click_through_rate != null ? parseFloat(responseObj.click_through_rate) : 0;
			dailyStatsObj.totalClicks = responseObj.total_clicks != null ? parseInt(responseObj.total_clicks, 10) : 0;
			dailyStatsObj.rtgImpressions =
				responseObj.rtg_impressions != null ? parseInt(responseObj.rtg_impressions, 10) : 0;
			dailyStatsObj.vtr = responseObj.view_through_rate != null ? parseFloat(responseObj.view_through_rate) : 0;
			dailyStatsObj.viewThroughs = responseObj.view_throughs != null ? parseInt(responseObj.view_throughs, 10) : 0;
			dailyStatsObj.hovers = responseObj.hovers != null ? parseInt(responseObj.hovers, 10) : 0;
			dailyStatsObj.videoPlays = responseObj.video_plays != null ? parseInt(responseObj.video_plays, 10) : 0;
			dailyStatsObj.adInteractionRate =
				responseObj.ad_interaction_rate != null ? parseFloat(responseObj.ad_interaction_rate) : 0;
			dailyStatsObj.adInteractions =
				responseObj.ad_interactions != null ? parseInt(responseObj.ad_interactions, 10) : 0;
			dailyStatsObj.completions = responseObj.completions != null ? parseInt(responseObj.completions, 10) : 0;
			dailyStatsObj.completionRate =
				responseObj.completions_rate != null ? parseFloat(responseObj.completions_rate) : 0;
			this.dailyStats.push(dailyStatsObj);
		}
	}

	buildSeriesWithToolTipInfo(infoObj: any, totalDataLength: number): any[] {
		const seriesData = [];
		for (let i = 0; i < totalDataLength; i++) {
			const tempObj: any = {};
			let toolTipHTML = '';

			for (const info of infoObj) {
				if (info.isSeriesData) {
					tempObj.y = info.data[i];
				}
				const value = info.data[i];
				toolTipHTML +=
					// tslint:disable-next-line: prefer-template
					'<div>' +
					info.name +
					' : ' +
					(this.isFloat(value) ? numeral(value).format('0,0.00') + '%' : numeral(value).format('0,0')) +
					'</div><br/>';
			}
			tempObj.toolTipData = toolTipHTML;
			seriesData.push(tempObj);
		}
		return seriesData;
	}

	protected generateChartObj(): void {
		Highcharts.chart({
			chart: {
				margin: [40, 80, 70, 80],
				renderTo: this.chartElement.nativeElement
			},
			credits: {
				enabled: false
			},
			legend: {
				align: 'center',
				floating: false,
				layout: 'horizontal',
				verticalAlign: 'bottom',
				x: 0,
				y: 0
			},
			plotOptions: {
				column: {
					stacking: 'normal'
				},
				series: {
					borderWidth: 0
				}
			},
			series: this.graphObj.series,
			subtitle: {
				text: null
			},
			title: {
				text: null
			},
			tooltip: {
				shared: false,
				formatter(): string {
					if (this.point['toolTipData']) {
						return `${this.x}<br/>${this.point['toolTipData']}`;
					}
					return `${this.x}<br/>${this.point.y}`;
				}
			},
			xAxis: {
				categories: this.graphObj.categories,
				labels: {
					step: this.graphObj.stepCount,
					formatter(): string {
						return moment(this.value).format('D-MMM');
					}
				}
			},
			yAxis: [
				{
					// Primary yAxis
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					title: {
						text: 'Impressions'
					}
				},
				{
					// Secondary yAxis
					gridLineDashStyle: 'Solid',
					labels: {
						format: '{value}'
					},
					opposite: true,
					title: {
						text: 'Rates'
					}
				}
			]
		});
		this.dynamicWidth();
	}

	protected buildChartSeriesObj(): any[] {
		const chartSeries: any = [];
		const totalImpressions = _.pluck(this.dailyStats, 'totalImpressions');
		const rtgImpressions = _.pluck(this.dailyStats, 'rtgImpressions');
		const ctrs = _.pluck(this.dailyStats, 'ctr');
		const clicks = _.pluck(this.dailyStats, 'totalClicks');
		const vtrs = _.pluck(this.dailyStats, 'vtr');
		const viewThroughs = _.pluck(this.dailyStats, 'viewThroughs');
		const adInteractionRates = _.pluck(this.dailyStats, 'adInteractionRate');
		const adInteractions = _.pluck(this.dailyStats, 'adInteractions');
		const completionRates = _.pluck(this.dailyStats, 'completionRate');
		const completions = _.pluck(this.dailyStats, 'completions');
		const hovers = _.pluck(this.dailyStats, 'hovers');
		const videoPlays = _.pluck(this.dailyStats, 'videoPlays');
		const totalDataLength = totalImpressions.length;

		const ctrObj: any = {};
		const ctrSeriesInfo = [
			{
				data: clicks,
				isSeriesData: false,
				name: 'Clicks'
			},
			{
				data: ctrs,
				isSeriesData: true,
				name: 'Click Through Rate'
			}
		];
		ctrObj.type = 'line';
		ctrObj.name = 'CTR';
		ctrObj.data = this.buildSeriesWithToolTipInfo(ctrSeriesInfo, totalDataLength);
		ctrObj.color = this.getColorFromColorConfig(2, 1);
		ctrObj.fillOpacity = 0.4;
		ctrObj.yAxis = 1;
		ctrObj.opposite = true;
		ctrObj.legendIndex = 2;
		ctrObj.maxPointWidth = 50;
		ctrObj.marker = {
			enabled: false,
			symbol: 'square'
		};

		const vtrObj: any = {};
		const vtrSeriesInfo = [
			{
				data: viewThroughs,
				isSeriesData: false,
				name: 'View Throughs'
			},
			{
				data: vtrs,
				isSeriesData: true,
				name: 'View Through Rate'
			}
		];
		vtrObj.type = 'line';
		vtrObj.name = 'VTR';
		vtrObj.data = this.buildSeriesWithToolTipInfo(vtrSeriesInfo, totalDataLength);
		vtrObj.color = this.getColorFromColorConfig(3, 1);
		vtrObj.fillOpacity = 0.4;
		vtrObj.yAxis = 1;
		vtrObj.opposite = true;
		vtrObj.legendIndex = 2;
		vtrObj.maxPointWidth = 50;
		vtrObj.marker = {
			enabled: false,
			symbol: 'square'
		};

		const completionRateObj: any = {};
		const completionSeriesInfo = [
			{
				data: completions,
				isSeriesData: false,
				name: 'Completions'
			},
			{
				data: completionRates,
				isSeriesData: true,
				name: 'Completion Rate'
			}
		];

		completionRateObj.type = 'line';
		completionRateObj.name = 'Completion Rate';
		completionRateObj.data = this.buildSeriesWithToolTipInfo(completionSeriesInfo, totalDataLength);
		completionRateObj.color = this.getColorFromColorConfig(2, 1);
		completionRateObj.fillOpacity = 0.4;
		completionRateObj.yAxis = 1;
		completionRateObj.opposite = true;
		completionRateObj.legendIndex = 2;
		completionRateObj.maxPointWidth = 50;
		completionRateObj.marker = {
			enabled: false,
			symbol: 'square'
		};

		const adInteractionObj: any = {};
		const adInteractionSeriesInfo = [
			{
				data: hovers,
				isSeriesData: false,
				name: 'Hovers'
			},
			{
				data: videoPlays,
				isSeriesData: false,
				name: 'Video Plays'
			},
			{
				data: adInteractionRates,
				isSeriesData: true,
				name: 'Ad Interaction Rate'
			}
		];
		adInteractionObj.type = 'line';
		adInteractionObj.name = 'Interaction Rate';
		adInteractionObj.data = this.buildSeriesWithToolTipInfo(adInteractionSeriesInfo, totalDataLength);
		adInteractionObj.color = this.getColorFromColorConfig(4, 1);
		adInteractionObj.fillOpacity = 0.4;
		adInteractionObj.yAxis = 1;
		adInteractionObj.opposite = true;
		adInteractionObj.legendIndex = 2;
		adInteractionObj.maxPointWidth = 50;
		adInteractionObj.marker = {
			enabled: false,
			symbol: 'square'
		};

		const totalImpressionsObj: any = {};
		const totalImpressionsInfo = [
			{
				data: totalImpressions,
				isSeriesData: true,
				name: this.getImpressionsName(' Impressions')
			}
		];
		totalImpressionsObj.type = 'column';
		totalImpressionsObj.name = this.getImpressionsName(' Impressions');
		totalImpressionsObj.data = this.buildSeriesWithToolTipInfo(totalImpressionsInfo, totalDataLength);
		totalImpressionsObj.color = this.getColorFromColorConfig(0, 1);
		totalImpressionsObj.fillOpacity = 0.4;
		totalImpressionsObj.legendIndex = 2;
		totalImpressionsObj.marker = {
			symbol: 'circle'
		};

		const rtgImpressionsObj: any = {};
		const rtgImpressionsInfo = [
			{
				data: rtgImpressions,
				isSeriesData: true,
				name: this.getImpressionsName(' Retargeting Impressions')
			}
		];
		rtgImpressionsObj.type = 'column';
		rtgImpressionsObj.name = this.getImpressionsName(' Retargeting Impressions');
		rtgImpressionsObj.data = this.buildSeriesWithToolTipInfo(rtgImpressionsInfo, totalDataLength);
		rtgImpressionsObj.color = this.getColorFromColorConfig(1, 1);
		rtgImpressionsObj.fillOpacity = 0.4;
		rtgImpressionsObj.legendIndex = 2;
		rtgImpressionsObj.marker = {
			symbol: 'circle'
		};

		if (this.analyticsDataModel.getCurrentPath() === PATHS.DISPLAY) {
			chartSeries.push(rtgImpressionsObj);
			chartSeries.push(totalImpressionsObj);
			chartSeries.push(adInteractionObj);
			chartSeries.push(ctrObj);
			chartSeries.push(vtrObj);
		} else {
			chartSeries.push(rtgImpressionsObj);
			chartSeries.push(totalImpressionsObj);
			chartSeries.push(completionRateObj);
		}
		return chartSeries;
	}

	private isFloat(n: any): boolean {
		return Number(n) === n && n % 1 !== 0;
	}
}
